// SourceCard.tsx

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useNavigate, useParams } from "react-router-dom";
import React, { JSX, useEffect } from "react";
import { cn } from "@/lib/utils";

// ** Old Source Type **
// export type Source = {
//   start_page: number;
//   end_page: number;
//   line_from: number;
//   line_to: number;
//   source: string;
//   bbox_left: number;
//   bbox_right: number;
//   bbox_top: number;
//   bbox_bottom: number;
//   bbox_coord_origin: string;
// }

export type Source = {
  start_page: number;
  end_page: number;
  source: string;
  bbox_coord_origin: string;
  bboxes?: {
    page: number;
    bbox_left: number;
    bbox_right: number;
    bbox_top: number;
    bbox_bottom: number;
  }[];
}

interface Props {
  source: string;
  onClick: () => void;
  sourceNumber: number;
  isActive?: boolean;
}

// Utility to extract file name from the source
export const getFileName = (source: string): string => {
  // Extract the file name from the full URL/path
  const fileName = source?.split("/").pop() || source;
  // Remove a leading UUID-like string (hex characters and dashes) ending with a hyphen
  return fileName?.replace(/^[a-f0-9-]+-/, "");
};

/**
 * SourceCard component displays a card with source information.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.source - The source file name or path.
 * @param {() => void} props.onClick - The click event handler for the card.
 * @param {number} props.sourceNumber - The source number.
 * @param {boolean} props.isActive - Whether the source is active.
 *
 * @returns {JSX.Element} The rendered SourceCard component.
 */
const SourceCard = (props: Props): JSX.Element => {

  const { source, onClick, sourceNumber, isActive = false } = props;

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger className="cursor-pointer group" onClick={onClick}>
        <div className={cn(
          "group-hover:bg-slate-400 text-[10px] rounded-full size-[28px] items-center flex justify-center group-active:scale-95 transition-all duration-200 select-none",
          isActive ? "bg-slate-400" : "bg-slate-200")}
        >
          {sourceNumber}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto text-xs font-normal p-2">
        {getFileName(source)}
      </HoverCardContent>
    </HoverCard>
  );
};

type ActiveSourceIdentifier = {
  sourceIndex: number;
  bboxIndex?: number;
}

/**
 * Component that renders a list of source cards.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.sources - An array of source objects.
 * @param {string} [props.qaSetId] - Unique identifier for the Q/A set containing this sources list.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @component
 *
 * @example
 * const sources = [
 *   { source: 'file1.pdf', page: 1, line_from: 10, line_to: 20 },
 *   { source: 'file2.pdf', page: 2, line_from: 15, line_to: 25 }
 * ];
 * return <SourcesList sources={sources} qaSetId="qa-1" />;
 */
const SourcesList = ({
  sources,
  qaSetId
}: {
  sources: Source[],
  qaSetId?: string
}): JSX.Element => {

  // Hooks
  const navigate = useNavigate();
  const { tenderID } = useParams<{ tenderID: string }>();
  const [activeSource, setActiveSource] = React.useState<ActiveSourceIdentifier | null>(null);
  const location = window.location;

  // Check URL params on mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Check if this Q/A set is active
    const activeQaSet = searchParams.get("qaSet");

    // If we have a qaSetId and it doesn't match the active one, don't activate any sources
    // This is the key to preventing multiple active sources across different Q/A sets
    if (qaSetId && activeQaSet && qaSetId !== activeQaSet) {
      setActiveSource(null);
      return;
    }

    // If the URL has no qaSetId but we have one, this source list shouldn't be active
    if (qaSetId && !activeQaSet) {
      setActiveSource(null);
      return;
    }

    const fileName = searchParams.get("file");
    const page = searchParams.get("page");

    // If we don't have at least file and page params, we can't determine the active source
    if (!fileName || !page) {
      setActiveSource(null);
      return;
    }

    // Check if we have bbox parameters (for sources with bboxes)
    const hasBboxParams = searchParams.has("bbox_left") ||
                          searchParams.has("bbox_right") ||
                          searchParams.has("bbox_top") ||
                          searchParams.has("bbox_bottom");

    const bboxLeft = searchParams.get("bbox_left");
    const bboxRight = searchParams.get("bbox_right");
    const bboxTop = searchParams.get("bbox_top");
    const bboxBottom = searchParams.get("bbox_bottom");

    // Find source and bbox that match the URL parameters
    for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
      const source = sources[sourceIndex];
      const sourceFileName = encodeURIComponent(getFileName(source.source));

      if (sourceFileName !== fileName) continue;

      // If source has bboxes array, check each bbox
      if (source.bboxes && source.bboxes.length > 0) {
        // If we have bbox parameters, try to match a specific bbox
        if (hasBboxParams) {
          const bboxIndex = source.bboxes.findIndex(bbox =>
            bbox.page.toString() === page &&
            (bboxLeft ? bbox.bbox_left.toString() === bboxLeft : true) &&
            (bboxRight ? bbox.bbox_right.toString() === bboxRight : true) &&
            (bboxTop ? bbox.bbox_top.toString() === bboxTop : true) &&
            (bboxBottom ? bbox.bbox_bottom.toString() === bboxBottom : true)
          );

          if (bboxIndex !== -1) {
            setActiveSource({ sourceIndex, bboxIndex });
            return;
          }
        }
      } else {
        // Legacy case: source doesn't have bboxes array
        // Just match on file and page (we don't need to check bbox params here)
        if (source.start_page.toString() === page) {
          setActiveSource({ sourceIndex });
          return;
        }
      }
    }

    // If no match found, clear active source
    setActiveSource(null);
  }, [location.search, sources, qaSetId]);

  /**
   * Handles the click event on a source card.
   *
   * This function builds a URL with the necessary parameters to navigate to the document
   * and highlight the specific section referenced by the source. It includes the file name,
   * page number, and bounding box coordinates if available.
   *
   * @param {Source} source - The source object containing document reference information
   * @param {number} [bboxIndex] - The index of the bbox in the source.bboxes array
   * @returns {void}
   */
  const handleSourceClick = (source: Source, bboxIndex?: number): void => {
    // Create a fresh URL with the base path
    const url = new URL(`/tender/${tenderID}/documentchat/`, window.location.origin);

    // Add file parameter (required)
    url.searchParams.set("file", encodeURIComponent(getFileName(source.source)));

    // Add qaSetId if available
    if (qaSetId) {
      url.searchParams.set("qaSet", qaSetId);
    }

    if (source.bboxes && source.bboxes.length > 0 && bboxIndex !== undefined && bboxIndex >= 0) {
      // For sources with bboxes array, use the specific bbox
      const bbox = source.bboxes[bboxIndex];

      // Set page and all bbox parameters
      url.searchParams.set("page", bbox.page.toString());
      url.searchParams.set("bbox_left", bbox.bbox_left.toString());
      url.searchParams.set("bbox_right", bbox.bbox_right.toString());
      url.searchParams.set("bbox_top", bbox.bbox_top.toString());
      url.searchParams.set("bbox_bottom", bbox.bbox_bottom.toString());

      // Add coordinate origin if available
      if (source.bbox_coord_origin) {
        url.searchParams.set("bbox_coord_origin", source.bbox_coord_origin);
      }
    } else {
      // For legacy sources without bboxes, just set the page
      url.searchParams.set("page", source.start_page.toString());
    }

    // Navigate to the URL
    navigate(url.pathname + url.search);
  };

  /**
   * Renders source cards for each source and each bbox if available
   */
  const renderSourceCards = () => {
    let cardIndex = 0;
    const cards: JSX.Element[] = [];

    sources.forEach((source, sourceIndex) => {
      if (source.bboxes && source.bboxes.length > 0) {
        // Render one card per bbox if bboxes array exists and is not empty
        source.bboxes.forEach((bbox, bboxIndex) => {
          const isActive = activeSource?.sourceIndex === sourceIndex && activeSource?.bboxIndex === bboxIndex;
          const cardNumber = ++cardIndex;

          cards.push(
            <div key={`${sourceIndex}-${bboxIndex}-${source?.source}`} className="w-fit h-auto">
              <SourceCard
                source={source?.source}
                sourceNumber={cardNumber}
                onClick={() => handleSourceClick(source, bboxIndex)}
                isActive={isActive}
              />
            </div>
          );
        });
      } else {
        // Render one card per source for legacy sources without bboxes
        const isActive = activeSource?.sourceIndex === sourceIndex && activeSource?.bboxIndex === undefined;
        const cardNumber = ++cardIndex;

        cards.push(
          <div key={`${sourceIndex}-${source?.source}`} className="w-fit h-auto">
            <SourceCard
              source={source?.source}
              sourceNumber={cardNumber}
              onClick={() => handleSourceClick(source)}
              isActive={isActive}
            />
          </div>
        );
      }
    });

    return cards;
  };

  return (
    <div className="flex flex-wrap gap-2">
      {renderSourceCards()}
    </div>
  );
};

export default SourcesList;
